import React, { createContext, useState, useEffect, Suspense, useLayoutEffect } from "react";
// router
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { createBrowserHistory } from "history";

// redux
import { useDispatch, useSelector } from "react-redux";
import { userType as userTypeData } from "./@slice/userSlice";
import { increment, needLogin as needLoginData } from "./@slice/globalSlice";

// react-query
import ReactQueryProvider from "./queries/ReactQueryProvider";

// types
import { IMe } from "./api/common";

// utils
import dayjs from "dayjs";
import "dayjs/locale/ko";
import { getOs, GA_TRACKING_ID, isWebview } from "./utils/index";
import useUrlQueryString from "./utils/hooks/useUrlQueryString";

// hooks
import { useWindowSize } from "./utils/hooks/useWindowSize";
import useInitUser from "./utils/hooks/useInitUser";

// ga
import ReactGA from "react-ga4";

// Common Components
import ScrollTopButton from "./components/common/buttons/ScrollTopButton"; // 상단 이동버튼
import AppQuickButtonContainer from "./components/common/quickButton/AppQuickButtonContainer"; // 컨설팅 퀵버튼
import ToastContainer from "./components/common/toast/ToastContainer";
import NeedLogin from "./components/common/needLogin/NeedLogin";
import LoadingSpinner from "./components/common/loading/LoadingSpinner";

// routes
import { indexRoutes } from "./routes/Index";
import RecommendRoute from "./routes/Recommend";
import UmomentRoute from "./routes/Umoment";
import { bookingRoutes } from "./routes/Booking";
import { realtimeBookingRoutes } from "./routes/RealtimeBooking";
import { plannerRoutes } from "./routes/Planner"; // 플래너
import { mypageRoutes } from "./routes/Mypage"; // 마이페이지 및 연결된 기타 내역 페이지들
import { authRoutes } from "./routes/Auth";
import { linkPayRoutes } from "./routes/LinkPay";
import { weddingStampRoutes } from "./routes/WeddingStamp";
import { productRoutes } from "./routes/Product";
import { ksnetPayRoutes } from "./routes/Ksnet";
import { partnerRoutes } from "./routes/Partner";
import { weddingHallRoutes } from "./routes/Weddinghall";
import WeddinghallDetailRoutes from "./routes/WeddinghallDetail";
import HallDetail from "./pages/weddingHall/HallDetail";
import { eventRoutes } from "./routes/Event";
import { consultingRoutes } from "./routes/Consulting";
import { treatzSpaRoutes } from "./routes/TreatzSpa";
import { balanceGameRoutes } from "./routes/BalanceGame";
import { budgetRoutes } from "./routes/Budget";
import { reviewRoutes } from "./routes/Review";
import { myWeddingRoutes } from "./routes/MyWedding";
import { weeklyAlarmRoutes } from "./routes/WeeklyAlarm";
import { weddingInvitationRoutes } from "./routes/WeddingInvitation";
import { benefitRoutes } from "./routes/Benefit";

declare global {
  interface Window {
    logoutAction: any;
    wb_bridge: any;
    Kakao: any;
    isNotificationEnabledResult: any;
    checkAppVersionResult: any;
    webkit: any;
    kakaoPixel: (id: string) => {
      pageView: () => void;
      tutorial: () => void;
      viewContent: () => void;
      preparation: () => void;
      participation: (string?: string) => void;
      missionComplete: () => void;
    };
    fbq: (
      action: string,
      eventName?: string,
      parameters?: Record<string, any>
    ) => void;
  }
}

// 회원 initial 데이터
const initialData = {
  updated: false,
  loggedin: false,
  dDay: 0,
  me: null,
  bookieUnreadCount: 0,
  newPushTsp: new Date(0).toDateString(),
};

// UserContext - 회원 정보 useContext로 주입하여 전달
export const UserContext = createContext<IMe>(initialData);

// 회원 정보 인터페이스

// Suspense fallback 시작 시점을 추적하는 컴포넌트 (더 정확한 버전)
function SuspenseFallbackTracker() {
  useLayoutEffect(() => {
    const loadingDebug = document.getElementById("loading-debug");
    if (loadingDebug) {
      const currentTime = new Date().toISOString();
      const currentTimeMs = new Date().getTime();

      // 페이지 로드 시작 시간 계산
      const loadStart = performance.timing ? performance.timing.navigationStart : 0;
      const timeFromLoadStart = loadStart ? currentTimeMs - loadStart : 0;

      loadingDebug.textContent =
        loadingDebug.textContent +
        "\nSuspense fallback 렌더링: " +
        currentTime +
        " (+" +
        timeFromLoadStart +
        "ms)";
    }
  }, []);

  return <LoadingSpinner />;
}

// lazy import 적용 후 라우트 자연스러운 등장 효과 (더 정확한 버전)
function FadeInWrapper({ children }: { children: React.ReactNode }) {
  const [visible, setVisible] = useState(false);

  useLayoutEffect(() => {
    // Suspense fallback이 끝나고 실제 컴포넌트가 렌더링 완료된 시점 기록
    const loadingDebug = document.getElementById("loading-debug");
    if (loadingDebug) {
      loadingDebug.style.position = "absolute";
      const currentTime = new Date().toISOString();
      const currentTimeMs = new Date().getTime();

      // 페이지 로드 시작 시간 계산
      const loadStart = performance.timing ? performance.timing.navigationStart : 0;
      const timeFromLoadStart = loadStart ? currentTimeMs - loadStart : 0;

      loadingDebug.textContent =
        loadingDebug.textContent +
        "\nSuspense fallback 완료: " +
        currentTime +
        " (+" +
        timeFromLoadStart +
        "ms)" +
        "\n=== 토탈 로드 시간" +
        timeFromLoadStart +
        "ms ===";
    }
  }, []);
  useEffect(() => {
    setVisible(true);
  }, []);

  return (
    <div style={{ opacity: visible ? 1 : 0, transition: "opacity 0.15s ease-in-out" }}>
      {children}
    </div>
  );
}
// 최상단으로 이동
function ScrollToTop() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  return null;
}
// UI레이아웃 고정
function UILayoutFix({ children }: { children: any }) {
  const location = useLocation();

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;

    const adjustHeight = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        requestAnimationFrame(() => {
          const div = document.getElementsByClassName("layout-fix-button")[0] as HTMLDivElement;
          if (!div) return;

          if (div.classList.contains("with-app-header")) {
            div.style.height = window.innerHeight - 156 + "px";
          } else {
            div.style.height = window.innerHeight - 100 + "px";
          }
        });
      }, 100); // 100ms 디바운스
    };

    adjustHeight();

    const observer = new MutationObserver(adjustHeight);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [location]);

  return children;
}
// 쿼리리스너 (쿼리 appHeader 값에 따라 처리)
function QueryListener({ children }: { children: any }) {
  const query = useUrlQueryString();
  useEffect(() => {
    if (query.get("appHeader") === "hide") {
      document.getElementsByTagName("html")[0].classList.add("hide-app-header");
    } else {
      document.getElementsByTagName("html")[0].classList.remove("hide-app-header");
    }
  }, [query]);
  return children;
}
// 루트 트래커 - location 변경에 따라 GA 결과 전송 (pageview)
function RouteTracker({ children }: { children: any }) {
  const location = useLocation();
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!GA_TRACKING_ID) return;
    ReactGA.initialize(GA_TRACKING_ID);
    setInitialized(true);
  }, []);
  // location 변경 감지시 pageview 이벤트 전송
  useEffect(() => {
    if (initialized) {
      ReactGA.set({ page: location.pathname });
      ReactGA.send("pageview");
    }
  }, [initialized, location]);
  return children;
}
// dayjs global 한글 설정
dayjs.locale("ko");

// App
function App() {
  // os 상태 클래스로 추가
  useEffect(() => {
    document.getElementsByTagName("html")[0].classList.add(getOs());
  }, []);

  // 키보드 노출시 버튼 위치 조정
  const { width, height } = useWindowSize();
  useEffect(() => {
    if (window !== undefined && height !== undefined && window.visualViewport) {
      const MIN_KEYBOARD_HEIGHT = 200; // 키보드 높이로 예상되는 최소값..(혹시나 다른이유로 visualViewport 높이가 더 작아 질 수 있으니)
      const isKeyboardOpen =
        window.screen.height - MIN_KEYBOARD_HEIGHT > window.visualViewport.height;
      const div = document.getElementsByClassName("layout-fix-button")[0] as HTMLDivElement;
      if (!div) return;
      if (isKeyboardOpen) {
        div.classList.add("show-keyboard");
        document.body.classList.add("show-keyboard");
      } else {
        div.classList.remove("show-keyboard");
        document.body.classList.remove("show-keyboard");
      }
    }
  }, [width, height]);

  // 안드로이드 상태바 투명을 위해 사용했던 코드
  // 일단 중단
  // useEffect(() => {
  //   if (getOs() === "android" && window.wb_bridge?.getStatusBarHeight !== undefined) {
  //     const statusBarHeight = window.wb_bridge.getStatusBarHeight();
  //     document.documentElement.style.setProperty(
  //       "--androidStatusBarHeight",
  //       statusBarHeight + "px",
  //     );
  //   }
  // }, []);

  // 유저 정보 -> userAgent 따라 저장되는 유저 정보
  const [userInfo, setUserInfo] = useState<IMe>(initialData);

  // 유저 정보 -> 토큰에 따라 저장되는 유저 정보
  const userType = useSelector(userTypeData);

  // 로그인 필요 여부
  const needLogin = useSelector(needLoginData);

  useInitUser({
    userInfo,
    setUserInfo,
    initialData,
  });

  // 웹뷰 환경에서 네이티브 앱에 토큰 전달
  useEffect(() => {
    if (!isWebview()) return;

    const accessToken = localStorage.getItem("accessToken");
    const refreshToken = localStorage.getItem("refreshToken");
    if (!accessToken || !refreshToken) return;

    const tokenData = JSON.stringify({ accessToken, refreshToken });

    if (window.wb_bridge?.setTokens) {
      window.wb_bridge.setTokens(tokenData);
    } else if (window.webkit?.messageHandlers?.setTokens) {
      window.webkit.messageHandlers.setTokens.postMessage(tokenData);
    }
  }, []);

  const dispatch = useDispatch();

  useEffect(() => {
    // 최상단으로 이동
    window.scrollTo(0, 0);
    if (dispatch) {
      // for webview bridge
      (window as any).refreshMe = () => {
        dispatch(increment()); // for webview bridge
      };
    }
  }, [dispatch]);

  return (
    <div className="App">
      <ReactQueryProvider>
        <UserContext.Provider value={userInfo}>
          <BrowserRouter>
            <ScrollTopButton />
            <AppQuickButtonContainer />
            <ScrollToTop />
            <RouteTracker>
              <QueryListener>
                <UILayoutFix>
                  <Suspense fallback={<SuspenseFallbackTracker />}>
                    <FadeInWrapper>
                      <Routes>
                        {indexRoutes.map(({ path, element }, index) => (
                          <Route path={path} element={element} key={index} />
                        ))}
                        {/* 마이웨딩 */}
                        {myWeddingRoutes.map(({ path, element }, index) => (
                          <Route path={path} element={element} key={index} />
                        ))}
                        {/* 리뷰 */}
                        {reviewRoutes.map(({ path, element }, index) => (
                          <Route path={path} element={element} key={index} />
                        ))}

                        {/* /product/:id 어드민 품목 화면 */}
                        {productRoutes.map(({ path, element }, index) => (
                          <Route path={path} element={element} key={index} />
                        ))}

                        {/* ksnet 결제 */}
                        {ksnetPayRoutes.map(({ path, element }, index) => (
                          <Route path={path} element={element} key={index} />
                        ))}

                        {/* 업체, 파트너 */}
                        {partnerRoutes.map(({ path, element }, index) => (
                          <Route path={path} element={element} key={index} />
                        ))}

                        {/* 웨딩홀 검색, 탐색, 이벤트*/}
                        {weddingHallRoutes.map(({ path, element }, index) => (
                          <Route path={path} element={element} key={index} />
                        ))}
                        {/* 웨딩홀 상세 (위 라우트랑 순서 바뀌면 안됨) */}
                        <Route path="/weddinghall/*" element={<WeddinghallDetailRoutes />} />
                        <Route
                          path="/weddinghall/:uuid/detail/:individualHallUuid"
                          element={<HallDetail />}
                        />

                        {/* 회원가입 및 로그인 */}
                        {authRoutes.map(({ path, element }, index) => (
                          <Route path={path} element={element} key={index} />
                        ))}

                        {/* 플래너 */}
                        {plannerRoutes.map(({ path, element }, index) => (
                          <Route path={path} element={element} key={index} />
                        ))}

                        {/* 컨설팅 */}
                        {consultingRoutes.map(({ path, element }, index) => (
                          <Route path={path} element={element} key={index} />
                        ))}

                        {/* 혜택 */}
                        {benefitRoutes.map(({ path, element }, index) => (
                          <Route path={path} element={element} key={index} />
                        ))}

                        {/* 마이페이지 */}
                        {mypageRoutes.map(({ path, element }, index) => (
                          <Route path={path} element={element} key={index} />
                        ))}

                        {/* 실시간 방문 예약 */}
                        {realtimeBookingRoutes.map(({ path, element }, index) => (
                          <Route path={path} element={element} key={index} />
                        ))}
                        {/* 일반 방문 예약 */}
                        {bookingRoutes.map(({ path, element }, index) => (
                          <Route path={path} element={element} key={index} />
                        ))}

                        {/* 웨딩홀, 예물, 신혼여행 추천 신청 페이지 */}
                        <Route path="/recommend/*" element={<RecommendRoute />} />

                        {/* wedding stamp */}
                        {weddingStampRoutes.map(({ path, element }, index) => (
                          <Route path={path} element={element} key={index} />
                        ))}

                        {/* 유모멘트 */}
                        <Route path="/umoment/*" element={<UmomentRoute />} />

                        {/* 트리츠스파 */}
                        {treatzSpaRoutes.map(({ path, element }, index) => (
                          <Route path={path} element={element} key={index} />
                        ))}

                        {/* 밸런스게임 */}
                        {balanceGameRoutes.map(({ path, element }, index) => (
                          <Route path={path} element={element} key={index} />
                        ))}

                        {/* 링크 페이 */}
                        {linkPayRoutes.map(({ path, element }, index) => (
                          <Route path={path} element={element} key={index} />
                        ))}

                        {/* 이벤트s */}
                        {eventRoutes.map(({ path, element }, index) => (
                          <Route path={path} element={element} key={index} />
                        ))}

                        {/* 예산관리 */}
                        {budgetRoutes.map(({ path, element }, index) => (
                          <Route path={path} element={element} key={index} />
                        ))}

                        {/* 주단위 알림 */}
                        {weeklyAlarmRoutes.map(({ path, element }, index) => (
                          <Route path={path} element={element} key={index} />
                        ))}

                        {/* 청첩장 */}
                        {weddingInvitationRoutes.map(({ path, element }, index) => (
                          <Route path={path} element={element} key={index} />
                        ))}
                      </Routes>
                    </FadeInWrapper>
                  </Suspense>
                </UILayoutFix>
              </QueryListener>
            </RouteTracker>
            {/* accessToken 발급이 정상적으로 안 된 상태일때 로그인 필요 화면 노출 */}
            {userType !== "COMPLETE_AUTH_USER" && needLogin && <NeedLogin />}
            <ToastContainer />
          </BrowserRouter>
        </UserContext.Provider>
      </ReactQueryProvider>
    </div>
  );
}

export default App;
